<script setup>
import { getCurrentInstance, computed, onMounted, ref } from 'vue';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Paginator from 'primevue/paginator';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import { useToast } from 'primevue/usetoast';
import { useForm } from '@inertiajs/vue3';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import SiteCartButton from './SiteCartButton.vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

// Row expansion state
const expandedRows = ref([]);
const siteDetailsLoading = ref({});
const siteRecommendations = ref({});

// Notes state
const editingNotes = ref({});
const noteTexts = ref({});
const toast = useToast();

const noteForm = useForm({
    text: ''
});

const { formatConvertedPrice, getCurrentCurrency, onCurrencyChange } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

// Get calculated price for a specific type from backend data
const getTypePrice = (site, type) => {
    const uniqueOffer = site.unique_offers_by_type?.find(offer => offer.id === type.id);
    
    if (uniqueOffer) {
        return {
            price: uniqueOffer.converted_price,
            coin: uniqueOffer.converted_coin
        };
    }
    
    return { price: null, coin: null };
};

const sortedAllTypes = computed(() => {
    if (!props.allTypes || !Array.isArray(props.allTypes)) return [];
    return [...props.allTypes].sort((a, b) => {
        const ai = FIXED_NICHE_ORDER.indexOf(a.name?.toLowerCase().trim());
        const bi = FIXED_NICHE_ORDER.indexOf(b.name?.toLowerCase().trim());
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return (a.name || '').localeCompare(b.name || '');
    });
});

const visibleTypes = computed(() => sortedAllTypes.value.slice(0, NICHE_DISPLAY_LIMIT));
const extraTypesCount = computed(() => Math.max(0, sortedAllTypes.value.length - NICHE_DISPLAY_LIMIT));
const extraTypesTooltip = computed(() => sortedAllTypes.value.slice(NICHE_DISPLAY_LIMIT).map(t => t.name).join(', '));

const getTypeTooltip = (site, type) => {
  const isAllowed = site.offers?.some(o => o.type_id === type.id);

  let tooltip = type.name;

  if (isAllowed) {
    tooltip += ' ' + $t('related content is allowed');
  } else {
    tooltip += ' ' + $t('related content is not allowed');
  }

  return tooltip;
};

const props = defineProps({
    sites: {
        type: Object,
        required: true
    },
    sitesWithHighestSale: {
        type: Array,
        required: true
    },
    visibleColumns: {
        type: Array,
        required: true
    },
    isListLoading: {
        type: Boolean,
        default: false
    },
    parsedSort: {
        type: Object,
        required: true
    },
    coinStore: {
        type: Object,
        required: true
    },
    allTypes: {
        type: Array,
        default: () => []
    },
    // Context flags
    mode: {
        type: String,
        default: 'client', // 'admin' or 'client'
        validator: (value) => ['admin', 'client'].includes(value)
    },
    // Client-specific props
    favorites: {
        type: Array,
        default: () => []
    },
    interests: {
        type: Array,
        default: () => []
    },
    projects: {
        type: Array,
        default: () => []
    },
    // SVG Icons
    rocketSvg: {
        type: String,
        default: ''
    },
    rocketFillSvg: {
        type: String,
        default: ''
    },
    // Loading functions
    showPageLoading: {
        type: Function,
        default: () => {}
    },
    hidePageLoading: {
        type: Function,
        default: () => {}
    }
});

const emit = defineEmits([
    'sort',
    'page-change',
    'site-click',
    'toggle-favorite',
    'toggle-interest',
    'toggle-recommend',
    'add-to-project',
    'show-project',
    'remove-from-project',
    'edit-site',
    'blacklist-site',
    'open-site-details'
]);


const typeIconsMap = {
    'casinos': 'pi pi-box',
    'cbd': 'pi pi-building-columns',
    'crypto': 'pi pi-bitcoin',
    'dating': 'pi pi-heart',
    'erotic': 'pi pi-ban',
    'bet': 'pi pi-dollar',
    'loan': 'pi pi-credit-card',
    'forex': 'pi pi-chart-bar',
    'default': 'pi pi-circle',
    'none': 'pi pi-minus-circle',
    'newtype': 'pi pi-plus-circle',
    '_fallback': 'pi pi-tag'
};

const FIXED_NICHE_ORDER = ['casinos', 'cbd', 'crypto', 'dating', 'erotic', 'bet', 'loan', 'forex'];
const NICHE_DISPLAY_LIMIT = 10;

const getTypeIcon = (type) => {
    if (!type || !type.name) return typeIconsMap._fallback;

    const typeName = type.name.toLowerCase().trim();

    // Don't show icon for default type
    if (typeName === 'default') return null;

    return type.icon_name || typeIconsMap[typeName] || typeIconsMap._fallback;
};

// Helper functions
const isFavorite = (siteId) => {
    return props.favorites && props.favorites.includes(siteId);
}

const isInterest = (siteId) => {
    return props.interests && props.interests.includes(siteId);
}

const isSiteInProjects = (siteId) => {
    const site = props.sitesWithHighestSale.find(s => s.id === siteId);
    return site && site.project_ids && site.project_ids.toString().length > 0;
}

const getSiteProjects = (siteId) => {
    const site = props.sitesWithHighestSale.find(s => s.id === siteId);
    if (!site || !site.project_ids) return [];

    const projectIds = site.project_ids.toString().split(',').map(id => parseInt(id));
    return props.projects.filter(p => projectIds.includes(p.id));
}

const formatLinkType = (linkType) => {
    if (!linkType) return 'NOFOLLOW';

    const normalizedType = linkType.toUpperCase().trim();

    if (normalizedType.includes('DOFOLLOW')) {
        return 'DOFOLLOW';
    }

    if (normalizedType.includes('SPONSOR')) {
        return 'SPONSORED';
    }

    return 'NOFOLLOW';
}

const openExternalSite = (url) => {
    try {
        if (!url) {
            console.error('No URL provided');
            return;
        }

        const cleanUrl = url.replace(/^https?:\/\//, '').trim();

        if (!cleanUrl) {
            console.error('Invalid URL after cleaning:', url);
            return;
        }

        const fullUrl = `https://${cleanUrl}`;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
        console.error('Error opening external site:', e, 'URL:', url);
    }
}

// Pagination handlers
const handlePreviousPage = () => {
    if (props.sites.current_page > 1) {
        emit('page-change', { direction: 'prev', page: props.sites.current_page - 1 });
    }
}

const handleNextPage = () => {
    if (props.sites.current_page < props.sites.last_page) {
        emit('page-change', { direction: 'next', page: props.sites.current_page + 1 });
    }
}

const handlePageChange = (event) => {
    emit('page-change', { event, page: event.page + 1 });
}

const onSort = (event) => {
    emit('sort', event);
}

// Row expansion handler for desktop table
const onRowExpand = async (event) => {
    const site = event.data;
    // Fetch site details with recommendations if not already loaded
    if (!siteRecommendations.value[site.id]) {
        await fetchSiteDetails(site.id);
    }
}

// Row expansion handler for mobile cards
const toggleRowExpansion = async (site) => {
    // Ensure expandedRows is an array
    if (!Array.isArray(expandedRows.value)) {
        expandedRows.value = [];
    }

    const isExpanded = isRowExpanded(site.id);

    if (isExpanded) {
        // Collapse: remove from expanded rows
        expandedRows.value = expandedRows.value.filter(row => row && row.id !== site.id);
    } else {
        // Expand: add to expanded rows and fetch details if needed
        expandedRows.value = [...expandedRows.value, site];

        // Fetch site details with recommendations if not already loaded
        if (!siteRecommendations.value[site.id]) {
            await fetchSiteDetails(site.id);
        }
    }
}

// Fetch site details
const fetchSiteDetails = async (siteId) => {
    try {
        siteDetailsLoading.value[siteId] = true;

        // Determine the correct route based on mode
        let routeName = 'client.sites.details';
        if (props.mode === 'admin') {
            routeName = 'sites.details';
        }

        const response = await fetch(
            proxy.route ? proxy.route(routeName, siteId) : `/sites/${siteId}/details`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            }
        );

        if (response.ok) {
            const data = await response.json();
            siteRecommendations.value[siteId] = data.recommendations || [];
        }
    } catch (error) {
        console.error('Error fetching site details:', error);
    } finally {
        siteDetailsLoading.value[siteId] = false;
    }
}

// Get locale based on currency
const getLocaleForCurrency = (currency) => {
    const localeMap = {
        'BRL': 'pt-BR',
        'USD': 'en-US',
        'EUR': 'de-DE',
        'GBP': 'en-GB',
    };
    return localeMap[currency] || 'en-US';
}

// Format price helper
const formatPrice = (price, coin) => {
    if (!price) return '-';
    const currency = coin || props.coinStore.coin;
    const locale = getLocaleForCurrency(currency);
    // Convert from cents to currency units
    const priceInUnits = price / 100;
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(priceInUnits);
}

// Helper to check if a row is expanded (safe check for array)
const isRowExpanded = (siteId) => {
    if (!expandedRows.value) return false;
    if (!Array.isArray(expandedRows.value)) return false;
    return expandedRows.value.some(row => row && row.id === siteId);
}

// Helper to close an expanded row safely
const closeExpandedRow = (siteId) => {
    if (!Array.isArray(expandedRows.value)) {
        expandedRows.value = [];
        return;
    }
    expandedRows.value = expandedRows.value.filter(row => row && row.id !== siteId);
}

// Notes functions
const toggleEditNote = (siteId, site) => {
    if (!noteTexts.value[siteId]) {
        noteTexts.value[siteId] = site.user_note?.text || '';
}

    editingNotes.value[siteId] = !editingNotes.value[siteId];

    if (!editingNotes.value[siteId]) {
        noteTexts.value[siteId] = site.user_note?.text || '';
    }
}

const saveNote = (siteId) => {
    let routeName = 'client.notes.update';
    if (props.mode === 'admin') {
        routeName = 'notes.update';
    }
    noteForm.text = noteTexts.value[siteId] || '';
    noteForm.put(route(routeName, { site: siteId }), {
        onSuccess: () => {
            editingNotes.value[siteId] = false;
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Note saved successfully'),
                life: 3000
            });
        },
    });
};

onMounted(() => {
    currentCurrency.value = getCurrentCurrency() || 'USD';
    
    onCurrencyChange(({ currency, symbol }) => {
        currentCurrency.value = currency;
    });
});
</script>

<template>
    <!-- Mobile: Card View -->
    <div class="block lg:hidden relative">
        <!-- Mobile Loading Overlay -->
        <div v-if="isListLoading" class="absolute inset-0 bg-white/80 z-40 flex items-center justify-center rounded-lg min-h-[200px]">
            <div class="flex flex-col items-center gap-4">
                <ProgressSpinner
                    style="width: 50px; height: 50px"
                    strokeWidth="4"
                    animationDuration=".5s"
                />
                <span class="text-sm text-surface-500">{{ $t('Loading sites...') }}</span>
            </div>
        </div>

        <div class="space-y-2 sm:space-y-3">
            <Card
                v-for="site in sitesWithHighestSale"
                :key="site.id"
                class="p-3 sm:p-4 cursor-pointer hover:shadow-lg transition-shadow"
                @click="toggleRowExpansion(site)"
            >
                <template #content>
                    <div class="flex justify-between items-start mb-2 sm:mb-3">
                        <div class="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <img
                                :src="`https://www.google.com/s2/favicons?domain=${site.url}`"
                                class="w-6 h-6 flex-shrink-0"
                            />
                            <div class="min-w-0 flex-1">
                                <div class="flex items-center gap-2">
                                    <h3 class="text-sm sm:text-base font-medium text-surface-900 truncate">{{ site.url }}</h3>
                                    <i
                                        v-if="mode === 'client' && isSiteInProjects(site.id)"
                                        class="pi pi-flag-fill text-orange-500 text-sm cursor-pointer"
                                        v-tooltip="$t('In your projects')"
                                        @click.stop="$emit('show-project', site.id)"
                                    ></i>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="text-xs sm:text-sm text-primary-600 font-medium">
                                        {{
                                            formatConvertedPrice(
                                                site.converted_sale || site.highest_sale,
                                                site.converted_sale_coin || site.highest_sale_coin,
                                                site.highest_sale,
                                                site.highest_sale_coin
                                            )
                                        }}
                                    </span>
                                    <!-- Add to Cart Button (Client Mode Only - Mobile) -->
                                    <SiteCartButton
                                        v-if="mode === 'client'"
                                        :site-id="site.id"
                                        :price="site.converted_sale || site.highest_sale"
                                        :coin="site.converted_sale_coin || site.highest_sale_coin"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center gap-1 flex-shrink-0 ml-2">
                            <!-- Admin Actions -->
                            <template v-if="mode === 'admin'">
                                <Button
                                    icon="pi pi-pencil"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="$emit('edit-site', site)"
                                />
                                <Button
                                    text
                                    rounded
                                    size="small"
                                    v-tooltip="$t('Recommend')"
                                    @click.stop="$emit('toggle-recommend', site)"
                                >
                                    <template #icon>
                                        <span class="inline-flex items-center justify-center w-4 h-4" v-html="!site.is_recommended ? rocketSvg : rocketFillSvg"></span>
                                    </template>
                                </Button>
                                <Button
                                    icon="pi pi-ban"
                                    text
                                    rounded
                                    size="small"
                                    severity="danger"
                                    v-tooltip="$t('Blacklist site')"
                                    @click.stop="$emit('blacklist-site', site)"
                                />
                                <Button
                                    icon="pi pi-external-link"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="() => openExternalSite(site.url)"
                                />
                            </template>

                            <!-- Client Actions -->
                            <template v-if="mode === 'client'">
                                <Button
                                    icon="pi pi-eye"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="$emit('site-click', site)"
                                />
                                <Button
                                    icon="pi pi-external-link"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="() => openExternalSite(site.url)"
                                    :title="$t('Visit Site')"
                                />
                            </template>
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm">
                        <div>
                            <span class="font-medium text-surface-700">{{ $t('DA') }}:</span>
                            <span class="text-surface-600 ml-1">{{ site.da != null ? Math.ceil(site.da) : '-' }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-surface-700">{{ $t('DR') }}:</span>
                            <span class="text-surface-600 ml-1">{{ site.dr != null ? Math.ceil(site.dr) : '-' }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-surface-700">{{ $t('Traffic') }}:</span>
                            <span class="text-surface-600 ml-1">{{ site.traffic != null ? Math.ceil(site.traffic) : '-' }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-surface-700">{{ $t('Category') }}:</span>
                            <span class="text-surface-600 ml-1">{{ site.category?.title ?? '-' }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-surface-700">{{ $t('Country') }}:</span>
                            <span class="text-surface-600 ml-1">{{ site.country?.name ?? '-' }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-surface-700">{{ $t('Language') }}:</span>
                            <span class="text-surface-600 ml-1">{{ site.language?.name ?? '-' }}</span>
                        </div>
                    </div>

                    <!-- Types Icons Row (Mobile) -->
                    <div v-if="sortedAllTypes.length > 0" class="flex items-center gap-2 mt-2 pt-2 border-t border-surface-200">
                        <span class="text-xs font-medium text-surface-700">{{ $t('Accepts niche') }}:</span>
                        <div class="flex items-center gap-1 flex-wrap">
                            <div
                                v-for="type in visibleTypes"
                                :key="type.id"
                                class="relative inline-flex items-center"
                                v-show="getTypeIcon(type)"
                            >
                                <i
                                    :class="[
                                        getTypeIcon(type),
                                        'text-sm',
                                        site.offers && site.offers.some(o => o.type_id === type.id)
                                            ? 'text-surface-700'
                                            : 'text-surface-300'
                                    ]"
                                    v-tooltip.top="getTypeTooltip(site, type)"
                                ></i>
                            </div>
                            <Tag
                                v-if="extraTypesCount > 0"
                                :value="`+${extraTypesCount} ${$t('more')}`"
                                severity="secondary"
                                class="text-xs cursor-pointer"
                                v-tooltip.top="extraTypesTooltip"
                            />
                        </div>
                    </div>

                    <!-- Client Mobile Actions -->
                    <div v-if="mode === 'client'" class="flex justify-between items-center mt-3 pt-3 border-t border-surface-200">
                        <div class="flex items-center gap-2">
                            <Button
                                :icon="isFavorite(site.id) ? 'pi pi-heart-fill' : 'pi pi-heart'"
                                :severity="isFavorite(site.id) ? 'danger' : 'secondary'"
                                text
                                rounded
                                size="small"
                                @click.stop="$emit('toggle-favorite', site.id)"
                                :title="$t('Toggle Favorite')"
                            />
                            <Button
                                icon="pi pi-plus-circle"
                                severity="success"
                                text
                                rounded
                                size="small"
                                @click.stop="$emit('add-to-project', site.id)"
                                :title="$t('Add to Project')"
                            />
                            <Button
                                text
                                rounded
                                size="small"
                                v-tooltip="$t('Recommend')"
                                v-if="site.is_recommended"
                            >
                                <template #icon>
                                    <span class="inline-flex items-center justify-center w-4 h-4" v-html="rocketFillSvg"></span>
                                </template>
                            </Button>
                        </div>
                    </div>

                    <!-- Expanded Details for Mobile -->
                    <div v-if="isRowExpanded(site.id)" class="mt-4 pt-4 border-t border-surface-200">
                        <div v-if="siteDetailsLoading[site.id]" class="flex justify-center items-center py-8">
                            <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
                        </div>

                        <div v-else class="space-y-4">
                            <!-- Additional Site Information -->
                            <div class="space-y-3">
                                <!-- Price -->
                                <div class="border border-surface-200 rounded-lg p-3 bg-white">
                                    <label class="text-sm font-medium text-surface-700 block mb-1">{{ $t('Price') }}</label>
                                    <div class="text-lg font-semibold text-primary-600">
                                        {{
                                            formatConvertedPrice(
                                                site.converted_sale || site.highest_sale,
                                                site.converted_sale_coin || site.highest_sale_coin,
                                                site.highest_sale,
                                                site.highest_sale_coin
                                            )
                                        }}
                                    </div>
                                </div>

                                <!-- Average Publication Time -->
                                <div class="border border-surface-200 rounded-lg p-3 bg-white">
                                    <label class="text-sm font-medium text-surface-700 block mb-1">{{ $t('Average Publication Time') }}</label>
                                    <div class="text-surface-900">{{ site.average_publication_time || '-' }} {{ $t('days') }}</div>
                                </div>

                                <!-- Lifetime -->
                                <div class="border border-surface-200 rounded-lg p-3 bg-white">
                                    <label class="text-sm font-medium text-surface-700 block mb-1">{{ $t('Lifetime') }}</label>
                                    <div class="text-surface-900">{{ site.lifeTime || '-' }} {{ $t('months') }}</div>
                                </div>

                                <!-- Site Observations -->
                                <div v-if="site.obs" class="border border-surface-200 rounded-lg p-3 bg-white">
                                    <label class="text-sm font-medium text-surface-700 block mb-1">{{ $t('Site Observations') }}</label>
                                    <div class="text-surface-900 whitespace-pre-wrap">{{ site.obs }}</div>
                                </div>
                            </div>

                            <!-- Recommendations Section for Mobile -->
                            <div v-if="siteRecommendations[site.id] && siteRecommendations[site.id].length > 0" class="border-t pt-4 mt-4">
                                <h3 class="text-sm font-medium text-surface-700 mb-3 flex items-center px-2">
                                    <i class="pi pi-shopping-bag mr-2"></i>
                                    {{ $t('Customers who bought this also bought') }}
                                </h3>
                                <div class="space-y-2">
                                    <div
                                        v-for="recommendation in siteRecommendations[site.id]"
                                        :key="recommendation.id"
                                        class="border border-surface-200 rounded-lg p-3 bg-white hover:bg-surface-50 transition-colors"
                                        @click.stop="openExternalSite(recommendation.name)"
                                    >
                                        <div class="flex justify-between items-center">
                                            <div class="flex items-center gap-2">
                                                <img
                                                    :src="`https://www.google.com/s2/favicons?domain=${recommendation.name}`"
                                                    class="w-4 h-4"
                                                    :alt="recommendation.name"
                                                />
                                                <div>
                                                    <div class="text-sm font-medium text-surface-900">{{ recommendation.name }}</div>
                                                    <div class="text-xs text-primary-600">
                                                        {{ formatConvertedPrice(recommendation.price, recommendation.price_coin) }}
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                icon="pi pi-external-link"
                                                text
                                                rounded
                                                size="small"
                                                @click.stop="openExternalSite(recommendation.name)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Action Buttons for Mobile -->
                            <div class="flex justify-end gap-2 pt-3 border-t">
                                <Button
                                    :label="$t('Close')"
                                    @click.stop="toggleRowExpansion(site)"
                                    text
                                    size="small"
                                />
                                <Button
                                    :label="$t('Visit Site')"
                                    icon="pi pi-external-link"
                                    @click.stop="openExternalSite(site.url)"
                                    severity="primary"
                                    size="small"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Mobile Pagination -->
        <div class="flex justify-between items-center mt-3 sm:mt-4 gap-2">
            <Button
                :label="$t('Previous')"
                icon="pi pi-chevron-left"
                severity="secondary"
                size="small"
                :disabled="sites.current_page <= 1"
                @click="handlePreviousPage"
            />
            <span class="text-xs sm:text-sm text-surface-600 text-center px-2">
                {{ (sites.current_page - 1) * sites.per_page + 1 }} -
                {{ Math.min(sites.current_page * sites.per_page, sites.total) }}
                {{ $t('of') }} {{ sites.total }}
            </span>
            <Button
                :label="$t('Next')"
                icon="pi pi-chevron-right"
                iconPos="right"
                severity="secondary"
                size="small"
                :disabled="sites.current_page >= sites.last_page"
                @click="handleNextPage"
            />
        </div>
    </div>

    <!-- Desktop: Table View -->
    <div class="hidden lg:block relative overflow-hidden">
        <!-- Table Loading Overlay -->
        <div v-if="isListLoading" class="absolute inset-0 bg-white/80 z-40 flex items-center justify-center rounded-lg">
            <div class="flex flex-col items-center gap-4">
                <ProgressSpinner
                    style="width: 50px; height: 50px"
                    strokeWidth="4"
                    animationDuration=".5s"
                />
                <span class="text-sm text-surface-500">{{ $t('Loading sites...') }}</span>
            </div>
        </div>

        <div class="table-container">
            <DataTable
                :value="sitesWithHighestSale"
                :paginator="false"
                lazy
                scrollable
                scrollHeight="600px"
                scrollDirection="horizontal"
                @sort="onSort"
                sortMode="single"
                :sortField="parsedSort.field"
                :sortOrder="parsedSort.order"
                v-model:expandedRows="expandedRows"
                dataKey="id"
                @row-expand="onRowExpand"
                class="sites-datatable"
            >
            <template #empty>
                <div class="text-center py-12 text-surface-500">
                    {{ $t('No results with selected filters') }}
                </div>
            </template>
            <!-- Expander Column -->
            <Column expander style="width: 3rem" />

            <Column
                v-for="column in visibleColumns"
                :key="column.key"
                :field="column.key"
                :header="column.label"
                :sortable="column.sortable"
            >
                <template #body="slotProps">
                    <!-- Price Column -->
                    <div v-if="column.key === 'sale'" class="flex items-center gap-3">
                        <SiteCartButton
                            v-if="mode === 'client'"
                            :site-id="slotProps.data.id"
                            :price="slotProps.data.converted_sale || slotProps.data.highest_sale"
                            :coin="slotProps.data.converted_sale_coin || slotProps.data.highest_sale_coin"
                        />
                        <span class="font-medium">
                            {{
                                formatConvertedPrice(
                                    slotProps.data.converted_sale || slotProps.data.highest_sale,
                                    slotProps.data.converted_sale_coin || slotProps.data.highest_sale_coin,
                                    slotProps.data.highest_sale,
                                    slotProps.data.highest_sale_coin
                                )
                            }}
                        </span>
                    </div>

                    <!-- URL Column -->
                    <div v-else-if="column.key === 'url'" class="flex items-center gap-1 min-w-0">
                        <img
                            :src="`https://www.google.com/s2/favicons?domain=${slotProps.data.url}`"
                            class="w-4 h-4 flex-shrink-0"
                        />
                        <span class="text-sm truncate flex-1 min-w-0">{{ slotProps.data.url }}</span>

                        <div class="flex items-center gap-1 flex-shrink-0">
                            <!-- Admin URL Actions -->
                            <template v-if="mode === 'admin'">
                                <Button
                                    text
                                    rounded
                                    size="small"
                                    v-tooltip="$t('Recommend')"
                                    @click.stop="$emit('toggle-recommend', slotProps.data)"
                                >
                                    <template #icon>
                                        <span class="inline-flex items-center justify-center w-4 h-4" v-html="!slotProps.data.is_recommended ? rocketSvg : rocketFillSvg"></span>
                                    </template>
                                </Button>
                                <Button
                                    icon="pi pi-external-link"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="() => openExternalSite(slotProps.data.url)"
                                    :title="$t('Visit Site')"
                                />
                                <Button
                                    icon="pi pi-pencil"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="$emit('edit-site', slotProps.data)"
                                    :title="$t('Edit')"
                                />
                            </template>

                            <!-- Client URL Actions -->
                            <template v-if="mode === 'client'">
                                <Button
                                    text
                                    rounded
                                    size="small"
                                    v-tooltip="$t('Recommended')"
                                    v-if="slotProps.data.is_recommended"
                                >
                                    <template #icon>
                                        <span class="inline-flex items-center justify-center w-4 h-4" v-html="rocketFillSvg"></span>
                                    </template>
                                </Button>
                                <Button
                                    icon="pi pi-flag-fill"
                                    severity="warning"
                                    text
                                    rounded
                                    size="small"
                                    @click="() => $emit('show-project', slotProps.data.id)"
                                    v-tooltip="$t('In your projects')"
                                    v-if="isSiteInProjects(slotProps.data.id)"
                                />
                                <Button
                                    icon="pi pi-external-link"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="() => openExternalSite(slotProps.data.url)"
                                    :title="$t('Visit Site')"
                                />
                                <Button
                                    :icon="isFavorite(slotProps.data.id) ? 'pi pi-heart-fill' : 'pi pi-heart'"
                                    :severity="isFavorite(slotProps.data.id) ? 'danger' : 'secondary'"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="$emit('toggle-favorite', slotProps.data.id)"
                                    :title="$t('Toggle Favorite')"
                                />
                                <Button
                                    icon="pi pi-plus-circle"
                                    severity="success"
                                    text
                                    rounded
                                    size="small"
                                    @click.stop="$emit('add-to-project', slotProps.data.id)"
                                    :title="$t('Add to Project')"
                                />
                            </template>
                        </div>
                    </div>

                    <!-- DA Column -->
                    <span v-else-if="column.key === 'da'">{{ slotProps.data.da != null ? Math.ceil(slotProps.data.da) : '-' }}</span>

                    <!-- DR Column -->
                    <span v-else-if="column.key === 'dr'">{{ slotProps.data.dr != null ? Math.ceil(slotProps.data.dr) : '-' }}</span>

                    <!-- Traffic Column -->
                    <span v-else-if="column.key === 'traffic'">{{ slotProps.data.traffic != null ? Math.ceil(slotProps.data.traffic) : '-' }}</span>

                    <!-- Projects in Column (Client only) -->
                    <div v-else-if="column.key === 'projects_in' && mode === 'client'" class="flex flex-wrap gap-1">
                        <span
                            v-for="project in getSiteProjects(slotProps.data.id)"
                            :key="project.id"
                            class="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full group"
                            :style="{ backgroundColor: project.color + '20', color: project.color }"
                        >
                            {{ project.name }}
                            <button
                                @click.stop="$emit('remove-from-project', slotProps.data.id, project.id)"
                                class="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors"
                                :title="$t('Remove from project')"
                            >
                                <i class="pi pi-times text-xs font-bold"></i>
                            </button>
                        </span>
                        <span v-if="getSiteProjects(slotProps.data.id).length === 0" class="text-surface-500">-</span>
                    </div>

                    <!-- Gambling Column -->
                    <span v-else-if="column.key === 'gambling'">{{ $t(slotProps.data.gambling ? 'Yes' : 'No') }}</span>

                    <!-- Category Column -->
                    <span v-else-if="column.key === 'category'">{{ slotProps.data.category?.title ?? '-' }}</span>

                    <!-- Country Column -->
                    <span v-else-if="column.key === 'country'">{{ slotProps.data.country?.name ?? '-' }}</span>

                    <!-- Language Column -->
                    <span v-else-if="column.key === 'language'">{{ slotProps.data.language?.name ?? '-' }}</span>

                    <!-- Seller Column -->
                    <span v-else-if="column.key === 'seller'">{{ slotProps.data.seller?.name ?? '-' }}</span>

                    <!-- Types - Accepts niche - Column -->
                    <!-- Types - Accepts niche - Column with Icons -->
                    <div v-else-if="column.key === 'types'" class="flex items-center gap-2 flex-wrap min-w-20">
                        <template v-if="sortedAllTypes.length > 0">
                            <div
                                v-for="type in visibleTypes"
                                :key="type.id"
                                class="relative inline-flex items-center"
                                v-show="getTypeIcon(type)"
                            >
                                <i
                                    :class="[
                                        getTypeIcon(type),
                                        'text-base',
                                        slotProps.data.offers && slotProps.data.offers.some(o => o.type_id === type.id)
                                        ? 'text-surface-700'
                                        : 'text-surface-300'
                                    ]"
                                    v-tooltip.top="getTypeTooltip(slotProps.data, type)"
                                ></i>
                            </div>
                            <Tag
                                v-if="extraTypesCount > 0"
                                :value="`+${extraTypesCount} ${$t('more')}`"
                                severity="secondary"
                                class="text-xs cursor-pointer"
                                v-tooltip.top="extraTypesTooltip"
                            />
                        </template>
                        <span v-else-if="slotProps.data.offers && slotProps.data.offers.length > 0">
                            {{ [...new Set(slotProps.data.offers.map(o => o.type?.name).filter(Boolean))].join(', ') }}
                        </span>
                        <span v-else class="text-surface-400">-</span>
                    </div>

                    <!-- Link Type Column -->
                    <span v-else-if="column.key === 'link_type'" class="flex items-center gap-1 relative">
                        <span class="font-medium">
                            {{ formatLinkType(slotProps.data.link_type).toUpperCase() }}
                        </span>

                        <span
                            v-if="slotProps.data.type_observation"
                            class="flex items-center text-blue-500 cursor-pointer"
                            v-tooltip.top="slotProps.data.type_observation"
                        >
                            <i class="pi pi-info-circle" style="font-size: 0.7rem;"></i>
                        </span>
                    </span>

                    <!-- Branded Content Column -->
                    <span v-else-if="column.key === 'branded_content'">{{ $t(slotProps.data.branded_content ? 'Yes' : 'No') }}</span>

                    <!-- Obs Column -->
                    <span v-else-if="column.key === 'obs'">{{ slotProps.data.obs ?? '-' }}</span>

                    <!-- Example Column -->
                    <span v-else-if="column.key === 'example'">{{ slotProps.data.example ?? '-' }}</span>

                    <!-- Inserted At Column -->
                    <span v-else-if="column.key === 'inserted_at'">{{ slotProps.data.inserted_at ? new Date(slotProps.data.inserted_at).toLocaleDateString() : '-' }}</span>

                    <!-- Last Updated At Column -->
                    <span v-else-if="column.key === 'last_updated_at'">{{ slotProps.data.last_updated_at ? new Date(slotProps.data.last_updated_at).toLocaleDateString() : '-' }}</span>

                    <!-- Actions Column (Client only) -->
                    <div v-else-if="column.key === 'actions' && mode === 'client'" class="flex items-center gap-2">
                        <Button
                            :icon="isFavorite(slotProps.data.id) ? 'pi pi-heart-fill' : 'pi pi-heart'"
                            :severity="isFavorite(slotProps.data.id) ? 'danger' : 'secondary'"
                            text
                            rounded
                            size="small"
                            @click.stop="$emit('toggle-favorite', slotProps.data.id)"
                            :title="$t('Toggle Favorite')"
                        />
                        <Button
                            icon="pi pi-plus-circle"
                            severity="success"
                            text
                            rounded
                            size="small"
                            @click.stop="$emit('add-to-project', slotProps.data.id)"
                            :title="$t('Add to Project')"
                        />
                    </div>

                    <!-- Other columns (for dynamic type columns) -->
                    <span v-else>{{ slotProps.data[column.key] ?? '-' }}</span>
                </template>
            </Column>

            <!-- Row Expansion Template -->
            <template #expansion="slotProps">
                <div class="p-4 bg-surface-100">
                    <div v-if="siteDetailsLoading[slotProps.data.id]" class="flex justify-center items-center py-8">
                        <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
                    </div>

                    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-3 overflow-y-auto max-h-80 pb-4">
                        <!-- Site Information -->
                        <div class="space-y-3 border border-surface-200 rounded-lg p-4 bg-white">
                            <h3 class="text-sm font-medium text-surface-700 mb-3 flex items-center">
                                {{ $t('More info') }}
                            </h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <!-- Average Publication Time -->
                                <div class="bg-surface-50 rounded-lg p-2">
                                    <label class="text-sm font-medium text-surface-500 block mb-1">{{ $t('Average Publication Time') }}</label>
                                    <div class="text-surface-900 font-normal">{{ slotProps.data.average_publication_time || '-' }} {{ $t('days') }}</div>
                                </div>

                                <!-- Categories -->
                                <div class="bg-surface-50 rounded-lg p-2">
                                    <label class="text-sm font-medium text-surface-500 block mb-1">{{ $t('Categories') }}</label>
                                    <div class="text-surface-900 font-normal">
                                        <span v-if="slotProps.data.category">
                                            {{ slotProps.data.category.title }}
                                        </span>
                                        <span v-else class="text-surface-400">-</span>
                                    </div>
                                </div>

                                <!-- Lifetime -->
                                <div class="bg-surface-50 rounded-lg p-2">
                                    <label class="text-sm font-medium text-surface-500 block mb-1">{{ $t('Lifetime') }}</label>
                                    <div class="text-surface-900 font-normal">{{ slotProps.data.lifeTime || '-' }} {{ $t('months') }}</div>
                                </div>

                                <!-- Site Observations -->
                                <div class="bg-surface-50 rounded-lg p-2">
                                    <label class="text-sm font-medium text-surface-500 block mb-1">{{ $t('Site Observations') }}</label>
                                    <div class="text-surface-900 whitespace-pre-wrap text-sm">{{ slotProps.data.obs || '-' }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Types (Nichos) -->
                        <div class="space-y-3 border border-surface-200 rounded-lg p-4 bg-white">
                            <h3 class="text-sm font-medium text-surface-700 mb-3 flex items-center">
                                {{ $t('Accepted niches') }}
                            </h3>
                            <div class="border border-surface-200 rounded-lg p-2 bg-white gap-4 items-center min-h-11">
                                <div class="grid grid-cols-2 xl:grid-cols-2 gap-2">
                                    <span
                                        v-for="type in sortedAllTypes"
                                        :key="type.id"
                                        v-show="getTypeIcon(type)"
                                        :class="[
                                            'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm',
                                            slotProps.data.offers && slotProps.data.offers.some(o => o.type_id === type.id)
                                                ? 'bg-primary-50 text-primary-700'
                                                : 'bg-surface-50 text-surface-400'
                                        ]"
                                    >
                                        <i
                                            :class="[
                                                getTypeIcon(type),
                                                'text-sm',
                                                slotProps.data.offers && slotProps.data.offers.some(o => o.type_id === type.id)
                                                    ? 'text-primary-700'
                                                    : 'text-surface-400'
                                            ]"
                                        ></i>
                                        <span class="font-medium">{{ type.name }}</span>
                                        <span
                                            v-if="slotProps.data.offers && slotProps.data.offers.some(o => o.type_id === type.id)"
                                            class="text-primary-600 font-semibold"
                                        >
                                            {{
                                                (() => {
                                                    const typePrice = getTypePrice(slotProps.data, type);
                                                    return typePrice.price
                                                        ? '— ' + formatConvertedPrice(typePrice.price, typePrice.coin)
                                                        : '';
                                                })()
                                            }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- My Notes -->
                        <div class="space-y-2 border border-surface-200 rounded-lg p-2 bg-white">
                            <div class="top-section flex items-center justify-between">
                                <div>
                                    <h3 class="text-xs font-medium text-surface-700 mb-1 flex items-center">
                                        <i class="pi pi-file-edit mr-1 text-xs"></i>
                                        {{ $t('My Notes') }}
                                    </h3>
                                </div>
                                <div class="flex items-center gap-1">
                                    <Button
                                        v-if="editingNotes[slotProps.data.id]"
                                        icon="pi pi-save"
                                        :label="$t('Save')"
                                        :disabled="noteForm.processing"
                                        @click="saveNote(slotProps.data.id)"
                                        size="small"
                                        text
                                    />
                                    <Button
                                        :icon="editingNotes[slotProps.data.id] ? 'pi pi-times' : 'pi pi-pencil'"
                                        :label="editingNotes[slotProps.data.id] ? $t('Cancel') : $t('Edit')"
                                        @click="toggleEditNote(slotProps.data.id, slotProps.data)"
                                        :disabled="noteForm.processing"
                                        size="small"
                                        text
                                    />
                                </div>
                            </div>
                            <div class="rounded-lg p-1 bg-white gap-2 items-center">
                                <Textarea
                                    v-if="editingNotes[slotProps.data.id]"
                                    v-model="noteTexts[slotProps.data.id]"
                                    :placeholder="$t('Add your notes here...')"
                                    rows="3"
                                    class="w-full text-surface-900 text-sm"
                                    autoResize
                                    :disabled="noteForm.processing"
                                />
                                <div
                                    v-else
                                    class="min-h-[60px] p-2 border border-surface-200 rounded-lg bg-surface-50 text-surface-700 text-sm whitespace-pre-wrap break-words"
                                    :class="{ 'italic text-surface-400': !slotProps.data.user_note?.text }"
                                >
                                    {{ slotProps.data.user_note?.text || $t('No notes yet. Click Edit to add notes.') }}
                                </div>
                            </div>
                        </div>

                        <!-- Recommendations Section -->
                        <div v-if="siteRecommendations[slotProps.data.id] && siteRecommendations[slotProps.data.id].length > 0" class="border border-surface-200 rounded-lg p-4 bg-white lg:col-span-3">
                            <h3 class="text-sm font-medium text-surface-700 mb-3 flex items-center">
                                <i class="pi pi-shopping-bag mr-2"></i>
                                {{ $t('Customers who bought this also bought') }}
                            </h3>
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div
                                    v-for="recommendation in siteRecommendations[slotProps.data.id]"
                                    :key="recommendation.id"
                                    class="border border-surface-200 rounded-lg p-3 bg-white hover:bg-surface-50 transition-colors cursor-pointer"
                                    @click="openExternalSite(recommendation.name)"
                                >
                                    <div class="flex justify-between items-center">
                                        <div class="flex flex-col gap-2">
                                            <div class="flex items-center gap-1">
                                                <img
                                                    :src="`https://www.google.com/s2/favicons?domain=${recommendation.name}`"
                                                    class="w-5 h-5"
                                                    :alt="recommendation.name"
                                                />
                                                <div class="font-medium text-surface-900">{{ recommendation.name }}</div>
                                            </div>
                                            <div class="text-sm text-primary-600">
                                                {{ formatConvertedPrice(recommendation.price, recommendation.price_coin) }}
                                            </div>
                                            <div>
                                                <span
                                                    v-for="type in recommendation.types"
                                                    :key="type"
                                                    class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-50 text-primary-700 mr-2"
                                                >
                                                    {{ type }}
                                                </span>
                                            </div>
                                        </div>
                                        <Button
                                            icon="pi pi-external-link"
                                            text
                                            rounded
                                            size="small"
                                            @click.stop="openExternalSite(recommendation.name)"
                                            :title="$t('Visit Site')"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex justify-end gap-2 mx-2 lg:col-span-3">
                            <Button
                                :label="$t('Close')"
                                @click.stop="closeExpandedRow(slotProps.data.id)"
                                text
                            />
                            <Button
                                :label="$t('Visit Site')"
                                icon="pi pi-external-link"
                                @click.stop="openExternalSite(slotProps.data.url)"
                                severity="primary"
                            />
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <Paginator
                    v-if="sites?.per_page"
                    :rows="sites.per_page"
                    :totalRecords="sites.total || 0"
                    :first="(sites.current_page - 1) * sites.per_page"
                    @page="handlePageChange"
                />
            </template>
        </DataTable>
        </div>
    </div>
</template>

<style scoped>
/* Container for table to prevent page-level horizontal scroll */
.table-container {
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
}

/* Ensure table content doesn't break out */
.sites-datatable :deep(.p-datatable-wrapper) {
    overflow-x: auto;
    overflow-y: visible;
}

.sites-datatable :deep(.p-datatable-table) {
    min-width: max-content;
}
</style>
